import * as Yup from 'yup';
import { App } from 'App';
import { AppRegistrationSection } from 'AppRegistrationSection';
import { ButtonActor, InputActor } from 'components';
import { datadogLogs } from '@datadog/browser-logs';
import { FormWarning } from 'components/molecules';
import { Frame } from 'components/organisms';
import { frameConfig } from 'AppConfig';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { getScreenTranslation } from 'utils/translations';
import { isInWebView } from '../../utils/isInWebView';
import { isValid } from 'better-dni';
import {
  JUICY_NEXT_BTN,
  LOGIN_ROUTE,
  SESSION_MOBILE_INSTANCE_ID,
} from 'utils/constants';
import { LockIcon, ShieldIcon } from 'components/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import { passwordRegExp } from 'utils/regularExpressions';
import { routes } from '../../routes';
import { Row, Spacer } from 'components/atoms';
import { TestingContext } from 'InternalLibrary/testing';
import { useAnalytics } from 'use-analytics';
import { useAnalyticsCookies } from 'hooks/useAnalyticsCookies';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useErrorManager } from 'InternalLibrary/app';
import { useFormik } from 'formik';
import { useGetClientApplication, usePostLeadsRegister } from '../../api/atoms';
import { useMaybeNavigateByResolution } from '../../hooks/useMaybeNavigateByResolution';
import { ValidationFieldError } from '../../api';
import useClient from '../../hooks/useClient';

import { postMessageToApp } from '../../utils/postMessageToApp';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import FieldGroup from 'components/atoms/FieldGroup/FieldGroup';
import layout from 'scss/layout.module.scss';
import styles from './Confirmation.module.scss';
import Text from 'components/atoms/Typography/Text';
import Title from 'components/atoms/Title/Title';
import useAffiliateInfo from 'hooks/useAffiliateInfo';
import useAuth from 'hooks/useAuth';
import useCrossDomainLoginRedirect from 'hooks/useCrossDomainLoginRedirect';
import useIovation, { ioBlackBoxAtom } from 'hooks/useIovation';

export default function Confirmation() {
  const { login } = useAuth();
  const { setError } = useErrorManager();
  const apiLeadsRegister = usePostLeadsRegister();
  const { fetch: fetchGetClientApplication } = useGetClientApplication();
  const { fetch: fetchPostOnboardingRegister } = App.useApi(
    'onboarding/register',
    'POST',
  );
  const { maybeNavigateByResolution } = useMaybeNavigateByResolution();
  const navigate = useNavigate();
  const { maybeRedirectCrossDomain } = useCrossDomainLoginRedirect();
  const ioBlackBox = useRecoilValue(ioBlackBoxAtom);
  const [loading, setLoading] = useState(false);
  const fieldMatch: Record<string, string> = {
    email: 'id',
    personalId: 'id',
    mobilePhone: 'id',
  };
  const { track } = useAnalytics();
  const { setTestingData } = useContext(TestingContext);
  const { getAnalyticsCookies } = useAnalyticsCookies();
  const i18next = useTranslation();
  const { fetchClient } = useClient();

  const onSubmit = async (data: any) => {
    try {
      const applicationInstanceId = sessionStorage.getItem(
        SESSION_MOBILE_INSTANCE_ID,
      );

      const analyticsCookies = await getAnalyticsCookies();
      const { clientId } = await fetchPostOnboardingRegister({
        body: {
          personalId: data.id,
          password: data.password,
          passwordRepeat: data.password,
          email: leads?.email,
          mobilePhone: leads?.mobilePhone,
          source: 'DESKTOP',
          acceptAgreement: leads?.acceptAgreement,
          acceptNews: leads?.acceptNews,
          acceptDataSharing: leads?.acceptProfiling,
          productNumber: App.config.productNumber,
          ioBlackBox,
          affiliateInfo: affiliateInfo.provider ? affiliateInfo : undefined,
          analyticsCookies,
          applicationInstanceId,
        },
      });
      postMessageToApp('sign_up');

      setTestingData({
        clientID: String(clientId),
      });
      datadogLogs.setUser({
        id: clientId?.toString(),
      });

      await login(data.id, data.password);

      const clientUpdated = await fetchClient();

      track('identify', {
        client: clientUpdated,
        email: clientUpdated?.email?.email,
      });

      const application = await fetchGetClientApplication({
        headers: getFetchHeaders({
          Accept: 'application/vnd.4finance.web.v1.hal+json',
          'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
        }),
      });

      // Redirects to either vivusonline registration or account
      if (maybeRedirectCrossDomain(clientUpdated, application)) {
        return;
      }

      if (application?.status) {
        track('identify', {
          application,
        });
      }

      if (
        await maybeNavigateByResolution({ application, client: clientUpdated })
      ) {
        return;
      }

      sessionStorage.removeItem('lead');
      navigate(routes.FirstLoan_PersonalData);
    } catch (error) {
      // TODO: use addErrors instead.
      // addErrors
      // removeErrors
      if (error.name === 'ValidationError') {
        const errors = error.fieldErrors || error.errors;

        const isExistingUserError = Boolean(
          errors.find(
            (error: ValidationFieldError) =>
              error.messageTemplate === 'already_exists_in_db',
          ),
        );
        if (isExistingUserError) {
          formik.setStatus({
            errors: {
              _submit: [
                <FormWarning
                  text={i18next.t('notification:loginRegistrationError.text', {
                    login: LOGIN_ROUTE,
                    phone: i18next.t('common:contactPhone'),
                    'phone-no-spaces': i18next
                      .t('common:contactPhone')
                      .replace(/\s/g, ''),
                  })}
                  title={i18next.t('notification:loginRegistrationError.title')}
                />,
              ],
            },
          });
        } else {
          datadogLogs.logger.error(
            `Client account registration failed! - error: ${JSON.stringify(
              error,
              null,
              2,
            )}`,
          );
          errors.forEach(
            (error: { messageTemplate: string; property: string }) => {
              formik.setStatus({
                errors: {
                  [fieldMatch[error.property]]: [
                    i18next.t(
                      `notification:validation.${error.messageTemplate}.${error.property}`,
                    ),
                  ],
                },
              });
            },
          );
          setError(error);
        }
      } else {
        setError(error);
      }
    }
  };

  const content = getScreenTranslation('confirmation', i18next);
  const fieldGroupProps = {
    validationTextClassName: styles.validationLabel,
  };

  const ValidationSchema = Yup.object().shape({
    id: Yup.string()
      .required(i18next.t('validation:required_field'))
      .test('wrong_format', i18next.t('validation:wrong_format'), (value) =>
        value ? isValid(value) : false,
      ),
    password: Yup.string()
      .required(i18next.t('validation:required_field'))
      .min(5, i18next.t('validation:too_short'))
      .matches(passwordRegExp, i18next.t('validation:weak_password')),
  });

  const formik = useFormik({
    initialValues: {
      id: '',
      password: '',
    },
    validationSchema: ValidationSchema,
    onSubmit: async (data) => {
      formik.setStatus({ errors: {} });
      setLoading(true);
      await onSubmit(data);
      setLoading(false);
    },
  });

  useEffect(() => {
    setTestingData({});
  }, []);

  useIovation();

  const { affiliateInfo } = useAffiliateInfo();

  const leads = useMemo(() => {
    const apiLeadsRequestBody = apiLeadsRegister.request?.body;
    if (apiLeadsRequestBody) {
      return apiLeadsRequestBody;
    }

    const ssLeadStr = sessionStorage.getItem('lead');
    if (!ssLeadStr) {
      return null;
    }
    const { email, mobilePhone, agreements } = JSON.parse(ssLeadStr);

    return {
      email,
      mobilePhone,
      acceptNews: agreements?.firstAgreement,
      acceptProfiling: agreements?.secondAgreement,
      acceptAgreement: agreements?.thirdAgreement,
    };
  }, [apiLeadsRegister.request?.body]);

  if (
    !leads?.email ||
    !leads?.mobilePhone ||
    leads?.acceptAgreement === undefined ||
    leads?.acceptNews === undefined ||
    leads?.acceptProfiling === undefined
  ) {
    return <Navigate to={routes.FirstLoan_Email} />;
  }

  return (
    <Frame
      {...frameConfig}
      hideLogout
      sectionID={AppRegistrationSection.Identification}
    >
      <div className={layout['width-sm']}>
        <Spacer marginBottom="40px">
          <Title text={content.title} data-test="css-Create-Account-Title" />
        </Spacer>

        <form onSubmit={formik.handleSubmit}>
          <Spacer marginBottom="30px">
            <FieldGroup helperText={content.idInputLabel} {...fieldGroupProps}>
              <InputActor
                className={styles.border}
                name="id"
                label={content.idPlaceholder}
                inputProps={{
                  type: 'text',
                  dataTest: 'id-Id',
                  allowSpaces: false,
                }}
                formik={formik}
                sanitizer={(value: any) => value.toUpperCase().trim()}
                fieldGroupProps={{
                  dataTestValidationText: 'idInputValidationText',
                  ...fieldGroupProps,
                }}
                analytics={{
                  description: 'Personal ID (DNI/NIE)',
                }}
              />
            </FieldGroup>
          </Spacer>
          <Spacer marginBottom="30px">
            <FieldGroup
              helperText={content.passwordInputLabel}
              {...fieldGroupProps}
            >
              <InputActor
                className={styles.border}
                name="password"
                label={content.passwordPlaceholder}
                inputProps={{
                  type: 'password',
                  dataTest: 'id-Password',
                  allowSpaces: false,
                }}
                formik={formik}
                analytics={{
                  dontTrackContent: true,
                  description: 'Password',
                }}
                fieldGroupProps={{
                  dataTestValidationText: 'passwordInputValidationText',
                  ...fieldGroupProps,
                }}
              />
            </FieldGroup>
          </Spacer>
          <ButtonActor
            buttonProps={{
              className: JUICY_NEXT_BTN,
              leftIcon: <LockIcon />,
              type: 'submit',
              fill: true,
              loading,
              dataTest: 'xpath-Create-Account-Button',
            }}
            formik={formik}
            analytics={{
              description: 'Continue button',
            }}
          >
            {content.continueButtonText}
          </ButtonActor>
        </form>
        {!isInWebView() && (
          <Spacer marginTop="30px">
            <Row>
              <div className={styles['shield-icon-wrapper']}>
                <ShieldIcon className="test" />
              </div>
              <Text
                text={content.details}
                kind="p"
                level={3}
                className={styles['bottom-helper-text']}
                data-test="css-Secure-Info"
              />
            </Row>
          </Spacer>
        )}
      </div>
    </Frame>
  );
}
